import type { SelectedItem } from "@/contexts/SelectionContext";
import { useSelection } from "@/contexts/SelectionContext";
import { useMoveLessonToTrash } from "@/lib/hooks/lessons/useMoveLessonsToTrash";
import { useMoveProjectsToTrash } from "@/lib/hooks/projects/useMoveProjectsToTrash";
import { useNestSlides } from "@/lib/hooks/slides/useNestSlides";
import type { SlideWithSubslides } from "@/lib/hooks/slides/useSlidesByLessonId";
import { useAppDispatch } from "@/store/hooks";
import { openNestSlideModal } from "@/store/uiSlice";
import { resolveSlideTemplate } from "@ai-teach/types";
import { Button, IconButton } from "@ai-teach/ui-web";
import { HiX } from "react-icons/hi";
import {
  MdAssessment,
  MdContentCopy,
  MdDelete,
  MdDriveFileMove,
  MdLabel,
  MdOutlineVisibility,
  MdOutlineVisibilityOff,
  MdPostAdd,
  MdVerticalAlignTop,
} from "react-icons/md";

interface SelectionActionModalProps {
  page: "projectPage" | "lessonPage";
  isOpen: boolean;
  /** The lesson's main slides with their subslides, needed to judge what can be nested. */
  slides?: SlideWithSubslides[];
}

/** Mirrors the server's nesting rules so the button is disabled rather than failing on submit. */
function canBecomeSubslide(slide: SlideWithSubslides | undefined): boolean {
  if (!slide) return false;
  return (
    !slide.parentSlideId &&
    !slide.isLinkedCopy &&
    slide.linkedCopies.length === 0 &&
    slide.children.length === 0 &&
    resolveSlideTemplate(slide) !== "conversation"
  );
}

export function SelectionActionModal({ page, isOpen, slides = [] }: SelectionActionModalProps) {
  const {
    selectedLessons,
    selectedProjects,
    selectedSlides,
    clearAllSelections,
    toggleMoveModal,
    toggleMoveSlideModal,
    toggleTrashModal,
    toggleHideModal,
    toggleIsSelectionMode,
    toggleDuplicateSlideModal,
    toggleSetTagsModal,
    toggleSetDomainModal,
  } = useSelection();
  const dispatch = useAppDispatch();
  const { unnestSlides, loading: nestLoading } = useNestSlides();

  const isProjectPage = page === "projectPage";

  const selectedItemsCount = isProjectPage
    ? selectedLessons.length + selectedProjects.length
    : selectedSlides.length;

  const { loading: projectTrashLoading } = useMoveProjectsToTrash();
  const { loading: lessonTrashLoading } = useMoveLessonToTrash();

  const isLoading = lessonTrashLoading || projectTrashLoading;

  // Check if any selected item is a linked copy
  const hasLinkedCopyInSelection = isProjectPage
    ? [...selectedProjects, ...selectedLessons].some((item) => item.isLinkedCopy)
    : selectedSlides.some((s) => s.isLinkedCopy);

  // Every selected slide must be nestable on its own; one that is not would fail the whole batch.
  const selectedMainSlides = selectedSlides.map((selected) =>
    slides.find((slide) => slide.id === selected.id)
  );
  const canNestSelection =
    !isProjectPage && selectedSlides.length > 0 && selectedMainSlides.every(canBecomeSubslide);

  // Promoting is the inverse of nesting, so it replaces the button once every pick is a subslide.
  const subslideIds = new Set(slides.flatMap((slide) => slide.children).map((child) => child.id));
  const canPromoteSelection =
    !isProjectPage &&
    selectedSlides.length > 0 &&
    selectedSlides.every((selected) => subslideIds.has(selected.id));

  const promoteSelection = async () => {
    try {
      await unnestSlides(selectedSlides.map((selected) => selected.id));
    } finally {
      clearAllSelections();
      toggleIsSelectionMode(false);
    }
  };

  // Open the confirmation modal for a bulk visibility change (mirrors the Trash flow).
  // Linked-copy handling happens inside HideModal, so these buttons stay enabled.
  const openHideModal = (mode: "hide" | "show") => {
    toggleHideModal(mode);
    toggleIsSelectionMode(false);
  };

  if (!isOpen) return;

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[800]">
      <div className="bg-surface rounded-lg shadow-2xl border border-gray-800 px-6 py-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-secondary-200">{selectedItemsCount} selected</span>
          </div>
          <div className="flex items-center gap-3 border-l border-gray-800 pl-6">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                isProjectPage ? toggleMoveModal(true) : toggleMoveSlideModal(true);
                toggleIsSelectionMode(false);
              }}
              className="flex items-center gap-2"
              disabled={selectedItemsCount === 0 || hasLinkedCopyInSelection}
              title={
                hasLinkedCopyInSelection
                  ? "Cannot move linked copies. Unlink them first."
                  : undefined
              }
            >
              <MdDriveFileMove className="w-4 h-4" />
              Move
            </Button>
            {!isProjectPage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  toggleDuplicateSlideModal("multiple");
                  toggleIsSelectionMode(false);
                }}
                className="flex items-center gap-2"
                disabled={selectedItemsCount === 0 || hasLinkedCopyInSelection}
              >
                <MdContentCopy className="w-4 h-4" />
                Duplicate
              </Button>
            )}
            {!isProjectPage && canPromoteSelection && (
              <Button
                variant="ghost"
                size="sm"
                onClick={promoteSelection}
                className="flex items-center gap-2"
                disabled={nestLoading}
              >
                <MdVerticalAlignTop className="w-4 h-4" />
                Promote to slide
              </Button>
            )}
            {!isProjectPage && !canPromoteSelection && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  dispatch(
                    openNestSlideModal({
                      ids: selectedSlides.map((slide) => slide.id),
                      label: `${selectedSlides.length} ${selectedSlides.length === 1 ? "slide" : "slides"}`,
                    })
                  );
                  toggleIsSelectionMode(false);
                }}
                className="flex items-center gap-2"
                disabled={!canNestSelection}
                title={
                  canNestSelection
                    ? undefined
                    : "Only main slides with no subslides, no linked copies and no grammar conversation can become subslides."
                }
              >
                <MdPostAdd className="w-4 h-4" />
                Make subslide
              </Button>
            )}
            {!isProjectPage && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  toggleSetTagsModal(true);
                  toggleIsSelectionMode(false);
                }}
                className="flex items-center gap-2"
                disabled={selectedItemsCount === 0 || hasLinkedCopyInSelection}
                title={
                  hasLinkedCopyInSelection
                    ? "Cannot set tags on linked copies. Unlink them first."
                    : undefined
                }
              >
                <MdLabel className="w-4 h-4" />
                Tags
              </Button>
            )}
            {!isProjectPage && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  toggleSetDomainModal(true);
                  toggleIsSelectionMode(false);
                }}
                className="flex items-center gap-2"
                disabled={selectedItemsCount === 0 || hasLinkedCopyInSelection}
                title={
                  hasLinkedCopyInSelection
                    ? "Cannot set domain on linked copies. Unlink them first."
                    : undefined
                }
              >
                <MdAssessment className="w-4 h-4" />
                Domain
              </Button>
            )}
            {!isProjectPage && (
              <Button
                variant="default"
                size="sm"
                onClick={() => openHideModal("show")}
                className="flex items-center gap-2"
                disabled={selectedItemsCount === 0}
                aria-label="Show selected slides"
                title="Show"
              >
                <MdOutlineVisibility className="w-4 h-4" />
              </Button>
            )}
            {!isProjectPage && (
              <Button
                variant="default"
                size="sm"
                onClick={() => openHideModal("hide")}
                className="flex items-center gap-2"
                disabled={selectedItemsCount === 0}
                aria-label="Hide selected slides"
                title="Hide"
              >
                <MdOutlineVisibilityOff className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                toggleTrashModal(true);
                toggleIsSelectionMode(false);
              }}
              className="flex items-center gap-2"
              disabled={isLoading || selectedItemsCount === 0}
            >
              <MdDelete className="w-4 h-4" />
              Trash
            </Button>
          </div>
          <IconButton
            onClick={() => {
              clearAllSelections();
              toggleIsSelectionMode(false);
            }}
            variant="ghost"
            shape="circle"
            aria-label="Close selection"
          >
            <HiX className="w-5 h-5" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
